import { CloudArrowUpIcon as UploadIcon } from '@heroicons/react/24/outline';
import { default as Skeleton } from 'antd/es/skeleton';
import Dragger from 'antd/es/upload/Dragger';
import cn from 'classnames';

import type { Attachment } from 'api';
import { Restricted } from 'containers/Restricted';

import { TRANSLATIONS } from './constants';
import { DetailsAttachmentRow } from './DetailsAttachmentRow';

export const ATTACHMENTS_SECTION_TEST_ID = 'attachments-section';
export const ATTACHMENTS_UPLOAD_BUTTON_TEST_ID = 'attachments-upload-button';

type Props = {
  className?: string;
  items: Attachment[];
  isLoading: boolean;
  isFetching?: boolean;
  onDelete?: (uuid: string) => void;
  onDownload?: (att: Attachment) => void;
  downloadingUuid?: string | null;
  deletingUuid?: string | null;
  onUploadFile?: (file: File) => void;
  isUploading?: boolean;
};

export const DetailsAttachmentSection = ({
  className,
  items,
  isLoading,
  isFetching,
  onDelete,
  onDownload,
  downloadingUuid,
  deletingUuid,
  onUploadFile,
  isUploading
}: Props) => {
  return (
    <section
      data-testid={ATTACHMENTS_SECTION_TEST_ID}
      className={cn('break-words pb-0', className)}
    >
      <div
        className={cn('flex flex-col gap-3 overflow-y-auto', 'max-h-50', {
          'opacity-60': !!isFetching && !isLoading
        })}
      >
        {/* Upload */}
        <Restricted requiredPermission="attachments:manage">
          <div className="flex-column align-content-center flex w-full pb-0">
            <Dragger
              data-testid={ATTACHMENTS_UPLOAD_BUTTON_TEST_ID}
              name="file"
              className="upload-dragger w-full"
              multiple={false}
              showUploadList={false}
              disabled={isUploading}
              accept=".png,.jpg,.jpeg,.pdf"
              beforeUpload={(file) => {
                onUploadFile?.(file as File);
                return false;
              }}
            >
              <div className="flex flex-col items-center justify-center py-3">
                <UploadIcon className="size-8 stroke-current" />
                <div className="mt-2 text-sm">
                  {isUploading ? (
                    TRANSLATIONS.ATTACHMENT_UPLOADING
                  ) : (
                    <>
                      {TRANSLATIONS.ATTACHMENT_DRAG_OR}{' '}
                      <span className="link-underline cursor-pointer">
                        {TRANSLATIONS.ATTACHMENT_BROWSE}
                      </span>
                    </>
                  )}
                </div>
                <div className="mt-1 text-xs text-content-secondary">
                  {TRANSLATIONS.ATTACHMENT_SUPPORTED_FORMATS}
                </div>
              </div>
            </Dragger>
          </div>
        </Restricted>

        {/* Attachment List */}
        <div className="flex flex-col gap-1">
          {!isLoading && (
            <div className="flex flex-col gap-3 px-2 pb-2">
              {items.length === 0 ? (
                <span className="text-xs text-content-muted">
                  {TRANSLATIONS.ATTACHMENT_NO_FILES_UPLOADED}
                </span>
              ) : (
                <span className="text-sm font-medium">
                  {TRANSLATIONS.ATTACHMENT_UPLOADED_FILES(items.length)}
                </span>
              )}
            </div>
          )}

          <div
            className={cn('flex flex-col gap-3 overflow-y-auto', 'max-h-28', {
              'opacity-60': !!isFetching && !isLoading
            })}
          >
            {isLoading ? (
              <Skeleton
                active
                title={false}
                paragraph={{ rows: 1, width: '100%' }}
                className="!m-0"
              />
            ) : (
              items.length > 0 &&
              items.map((item) => (
                <DetailsAttachmentRow
                  key={item.uuid}
                  item={item}
                  onDelete={onDelete}
                  onDownload={onDownload}
                  isDownloading={downloadingUuid === item.uuid}
                  isDeleting={deletingUuid === item.uuid}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
